import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import CountUp from 'react-countup';
import { usePageHeader } from '../components/Layout';
import { SectionCard, AreaChart, Donut, Chip } from '../design/primitives';

const API = `${window.location.origin}/api`;

const ACTION_LABEL = {
  login: 'Giriş',
  logout: 'Çıkış',
  user_invite: 'Kullanıcı Daveti',
  user_update: 'Kullanıcı Güncelleme',
  user_delete: 'Kullanıcı Silme',
  role_change: 'Rol Değişikliği',
  password_reset: 'Şifre Sıfırlama',
  task_create: 'Görev Oluşturma',
  task_update: 'Görev Güncelleme',
  file_upload: 'Dosya Yükleme',
  file_delete: 'Dosya Silme',
};
const MODULE_LABEL = {
  auth: 'Kimlik Doğrulama',
  users: 'Kullanıcılar',
  tasks: 'Görevler',
  files: 'Dosyalar',
};

const DONUT_COLORS = ['#1E5FA8', '#6c4ca9', '#1a7f4f', '#a96b00', '#b4391e', '#a3470a', '#db2777', '#0891b2'];

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return (
    d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  );
}

function formatRelative(iso) {
  if (!iso) return '';
  const diffMin = Math.floor((new Date() - new Date(iso)) / 60000);
  if (diffMin < 1) return 'Az önce';
  if (diffMin < 60) return `${diffMin} dk önce`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h} sa önce`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} gün önce`;
  return new Date(iso).toLocaleDateString('tr-TR');
}

export default function AuditLog() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(null);

  const [selectedAction, setSelectedAction] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    if (!user || !['admin', 'proje_yoneticisi', 'super_admin'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 50 });
      if (selectedAction !== 'all') params.append('action', selectedAction);
      if (selectedModule !== 'all') params.append('module', selectedModule);
      if (selectedUser !== 'all') params.append('user_id', selectedUser);
      if (debounced) params.append('search', debounced);
      const res = await axios.get(`${API}/audit-logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
      setStats(res.data.stats);
    } catch {
      toast.error('Loglar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, selectedAction, selectedModule, selectedUser, debounced]);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit-logs/filters`);
      setFilters(res.data);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);
  useEffect(() => {
    setPage(1);
  }, [selectedAction, selectedModule, selectedUser, debounced]);

  const breadcrumb = useMemo(() => [{ label: 'Aktivite Günlüğü' }], []);
  const headerActions = useMemo(
    () => (
      <button
        className="ox-btn"
        onClick={() => {
          fetchLogs();
          fetchFilters();
        }}
      >
        <RefreshCw size={14} /> Yenile
      </button>
    ),
    [fetchLogs, fetchFilters]
  );
  usePageHeader(breadcrumb, headerActions, [fetchLogs, fetchFilters]);

  if (!user || !['admin', 'proje_yoneticisi', 'super_admin'].includes(user.role)) return null;

  const dailyTrendData =
    stats?.dailyTrend?.map((d) => ({ d: d._id?.slice(5) || '', v: d.count })) ||
    [];
  const moduleSegments =
    stats?.moduleBreakdown
      ? Object.entries(stats.moduleBreakdown).map(([k, v], i) => ({
          label: MODULE_LABEL[k] || k,
          value: v,
          color: DONUT_COLORS[i % DONUT_COLORS.length],
        }))
      : [];
  const topActionsRows =
    stats?.topActions
      ? Object.entries(stats.topActions)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
      : [];
  const hourlyData =
    stats?.hourlyDistribution?.map((h) => ({
      d: String(h._id).padStart(2, '0'),
      v: h.count,
    })) || [];

  return (
    <div className="ox-page space-y-5">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile
            label="Toplam Kayıt"
            value={<CountUp end={stats.totalLogs} duration={1.2} separator="." />}
          />
          <StatTile
            label="Bugün"
            value={<CountUp end={stats.todayCount} duration={1.2} separator="." />}
          />
          <StatTile
            label="Modüller"
            value={Object.keys(stats.moduleBreakdown || {}).length}
          />
          <StatTile
            label="İşlem Türü"
            value={Object.keys(stats.topActions || {}).length}
          />
        </div>
      )}

      {/* Filters */}
      <div className="ox-surface p-3 grid grid-cols-1 md:grid-cols-4 gap-2">
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]">
            <Search size={14} />
          </span>
          <input
            type="text"
            placeholder="Ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="ox-input pl-8"
          />
        </div>
        <select
          value={selectedModule}
          onChange={(e) => setSelectedModule(e.target.value)}
          className="ox-select"
        >
          <option value="all">Tüm Modüller</option>
          {filters?.modules?.map((m) => (
            <option key={m} value={m}>
              {MODULE_LABEL[m] || m}
            </option>
          ))}
        </select>
        <select
          value={selectedAction}
          onChange={(e) => setSelectedAction(e.target.value)}
          className="ox-select"
        >
          <option value="all">Tüm İşlemler</option>
          {filters?.actions?.map((a) => (
            <option key={a} value={a}>
              {ACTION_LABEL[a] || a}
            </option>
          ))}
        </select>
        <select
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
          className="ox-select"
        >
          <option value="all">Tüm Kullanıcılar</option>
          {filters?.users?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      {/* Charts */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <SectionCard title="Günlük Aktivite Trendi">
              {dailyTrendData.length > 0 ? (
                <AreaChart data={dailyTrendData} height={180} />
              ) : (
                <div className="text-center py-10 text-sm text-[color:var(--text-muted)]">
                  Henüz trend verisi yok
                </div>
              )}
            </SectionCard>
          </div>

          <SectionCard title="Modül Dağılımı">
            {moduleSegments.length > 0 ? (
              <div className="flex items-center gap-4">
                <Donut
                  segments={moduleSegments}
                  size={140}
                  thickness={14}
                  centerValue={moduleSegments.reduce((s, x) => s + x.value, 0)}
                  centerLabel="işlem"
                />
                <div className="flex-1 space-y-1">
                  {moduleSegments.map((s) => (
                    <div
                      key={s.label}
                      className="flex items-center gap-2 text-xs"
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ background: s.color }}
                      />
                      <span className="flex-1 truncate">{s.label}</span>
                      <span className="ox-mono">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-10 text-sm text-[color:var(--text-muted)]">
                Veri yok
              </div>
            )}
          </SectionCard>

          <div className="lg:col-span-2">
            <SectionCard title="Saatlik Dağılım">
              {hourlyData.length > 0 ? (
                <AreaChart data={hourlyData} height={150} color="var(--warn)" />
              ) : (
                <div className="text-center py-10 text-sm text-[color:var(--text-muted)]">
                  Veri yok
                </div>
              )}
            </SectionCard>
          </div>

          <SectionCard title="Popüler İşlemler">
            {topActionsRows.length > 0 ? (
              <div className="space-y-2">
                {topActionsRows.map(([k, v]) => {
                  const max = topActionsRows[0][1] || 1;
                  const pct = (v / max) * 100;
                  return (
                    <div key={k}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span>{ACTION_LABEL[k] || k}</span>
                        <span className="ox-mono">{v}</span>
                      </div>
                      <div className="ox-track">
                        <div
                          style={{
                            width: `${pct}%`,
                            height: '100%',
                            background: 'var(--accent)',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 text-sm text-[color:var(--text-muted)]">
                Veri yok
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {/* Log table */}
      <div className="ox-surface overflow-hidden">
        <div
          className="px-4 py-2.5 flex items-center justify-between"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold">Kayıtlar</span>
            <Chip mono>{total.toLocaleString('tr-TR')}</Chip>
          </div>
          {totalPages > 1 && (
            <div className="text-xs ox-text-muted">
              Sayfa{' '}
              <span className="ox-mono">
                {page}/{totalPages}
              </span>
            </div>
          )}
        </div>

        {loading && logs.length === 0 ? (
          <div className="p-10 text-center text-sm ox-text-muted">
            Yükleniyor...
          </div>
        ) : logs.length === 0 ? (
          <div className="p-10 text-center text-sm ox-text-muted">
            Kayıt bulunamadı.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="ox-surface-muted">
                  <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    Tarih
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    Kullanıcı
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    İşlem
                  </th>
                  <th className="hidden md:table-cell px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    Modül
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    Detay
                  </th>
                  <th className="hidden lg:table-cell px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    IP
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr
                    key={log.id}
                    className="ox-row-hover"
                    style={{ borderBottom: '1px solid var(--border)' }}
                  >
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="ox-mono text-xs">
                        {formatDate(log.createdAt)}
                      </div>
                      <div className="text-[10.5px] ox-text-dim">
                        {formatRelative(log.createdAt)}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="font-medium">{log.userName || '—'}</div>
                      <div className="text-[10.5px] ox-text-dim">
                        {log.userRole || ''}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <Chip>{ACTION_LABEL[log.action] || log.action}</Chip>
                    </td>
                    <td className="hidden md:table-cell px-3 py-2.5 whitespace-nowrap">
                      <span className="text-xs">
                        {MODULE_LABEL[log.module] || log.module || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div
                        className="text-xs max-w-xs truncate"
                        title={log.details}
                      >
                        {log.details || '—'}
                      </div>
                    </td>
                    <td className="hidden lg:table-cell px-3 py-2.5 whitespace-nowrap">
                      <span className="text-[11px] ox-mono ox-text-dim">
                        {log.ipAddress || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div
            className="flex items-center justify-between px-3 py-2.5 ox-surface-muted"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <div className="text-xs ox-text-muted">
              Toplam <span className="ox-mono">{total.toLocaleString('tr-TR')}</span> kayıt
            </div>
            <div className="flex items-center gap-1">
              <button
                className="ox-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={14} /> Önceki
              </button>
              <button
                className="ox-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="ox-surface p-4">
      <div className="text-[11px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide">
        {label}
      </div>
      <div className="text-2xl font-semibold ox-mono mt-1">{value}</div>
    </div>
  );
}
